/**
 * Image URL normalizer (client mirror of functions/normalizeImageUrl.js).
 *
 * Keeps stored references portable across the localhost dev host and the
 * production usvipiratesguild.com host. Defensive render-time normalizer
 * (`resolveImageSrc`) lets legacy records (with absolute localhost URLs
 * baked in) render correctly *before* a migration sweep runs.
 *
 * Rules mirror the server (intentionally permissive: silently returns
 * null instead of throwing — the server already enforces at write time).
 */

const IMAGE_HOST_STRIP = new Set([
  "localhost",
  "127.0.0.1",
  "0.0.0.0",
  "usvipiratesguild.com",
  "www.usvipiratesguild.com",
  "thepiratesguild-50ed5.web.app",
  "thepiratesguild-50ed5.firebaseapp.com",
]);

const IMAGE_HOST_ALLOW_ABSOLUTE = new Set([
  "firebasestorage.googleapis.com",
  "storage.googleapis.com",
  "lh3.googleusercontent.com",
]);

const PRIVATE_HOST_RE = /^(?:::1|10\.\d+\.\d+\.\d+|192\.168\.\d+\.\d+|172\.(?:1[6-9]|2\d|3[0-1])\.\d+\.\d+|169\.254\.\d+\.\d+)$/;

function isStripHost(hostname) {
  if (!hostname) return false;
  if (IMAGE_HOST_STRIP.has(hostname)) return true;
  if (PRIVATE_HOST_RE.test(hostname)) return true;
  return false;
}

function isAllowedAbsoluteHost(hostname) {
  return IMAGE_HOST_ALLOW_ABSOLUTE.has(hostname);
}

/**
 * Normalize a single image URL. Returns canonical form (bare path or
 * allowed absolute URL) or null when the value is unusable.
 */
export function normalizeImageUrl(value) {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw || raw.length > 2000) return null;

  if (raw.startsWith("/") && !raw.startsWith("//")) {
    if (raw.includes("..")) return null;
    return raw;
  }

  let url;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;

  if (isStripHost(url.hostname)) {
    return (url.pathname || "/") + (url.search || "");
  }
  if (isAllowedAbsoluteHost(url.hostname)) {
    return url.toString();
  }
  return null;
}

/**
 * Render-time resolver. Returns a string safe to drop into an `<img src>`:
 * an empty string for invalid input (browser will hit the placeholder
 * fallback in OptimizedImage), the bare path (browser resolves against
 * current origin → works on both localhost and prod), or an allowed
 * absolute URL.
 */
export function resolveImageSrc(value) {
  const out = normalizeImageUrl(value);
  return out == null ? "" : out;
}

/**
 * Build an absolute URL from a stored image reference. Used for things
 * like Open Graph `og:image` where a relative path would not be honored
 * by social-media crawlers.
 */
export function absoluteImageUrl(value, originOverride) {
  const resolved = resolveImageSrc(value);
  if (!resolved) return "";
  if (resolved.startsWith("/")) {
    const origin = originOverride
      || (typeof window !== "undefined" && window.location ? window.location.origin : "");
    return origin ? `${origin}${resolved}` : resolved;
  }
  return resolved;
}

const _exports = { normalizeImageUrl, resolveImageSrc, absoluteImageUrl };
export default _exports;
